import React, { useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";

export interface DerivedAttributeInputProps {
    value: string;
    onChange: (name: string) => void;
    /** Existing derived attribute names to show in the dropdown when focused. */
    existingNames?: string[];
    label?: string;
    placeholder?: string;
    size?: "small" | "medium";
    fullWidth?: boolean;
    disabled?: boolean;
    /** When true, onChange is only called on Enter or when selecting from the list, not on every keystroke. */
    commitOnly?: boolean;
    sx?: SxProps<Theme>;
    "data-testid"?: string;
}

/**
 * Single input that on focus shows a list of existing derived attribute names.
 * User can select one from the list or type a new name and press Enter to use it.
 */
export const DerivedAttributeInput: React.FC<DerivedAttributeInputProps> = ({
    value,
    onChange,
    existingNames = [],
    label = "Derived attribute",
    placeholder = "Select or type name and press Enter",
    size = "small",
    fullWidth = true,
    disabled = false,
    commitOnly = false,
    sx,
    "data-testid": dataTestId,
}) => {
    const [localInput, setLocalInput] = useState(value);
    useEffect(() => {
        setLocalInput(value);
    }, [value]);

    const commit = (v: string) => {
        const trimmed = v.trim();
        onChange(trimmed);
        if (commitOnly) setLocalInput(trimmed);
    };

    const displayValue = commitOnly ? localInput : value;
    const handleInputChange = (inputValue: string) => {
        if (commitOnly) {
            setLocalInput(inputValue);
        } else {
            onChange(inputValue ?? "");
        }
    };
    const handleChange = (_: unknown, newValue: string | null) => {
        const next = typeof newValue === "string" ? newValue : (newValue ? String(newValue) : "");
        commit(next);
    };

    return (
        <Autocomplete
            freeSolo
            size={size}
            fullWidth={fullWidth}
            disabled={disabled}
            sx={sx}
            options={existingNames}
            value={displayValue || null}
            inputValue={displayValue}
            onInputChange={(_, inputValue) => handleInputChange(inputValue ?? "")}
            onChange={handleChange}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    data-testid={dataTestId}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            const target = e.target as HTMLInputElement;
                            commit(target?.value ?? "");
                        }
                        params.inputProps.onKeyDown?.(e as any);
                    }}
                />
            )}
        />
    );
};
